import React from "react";
import DataTable from "components/common/table/DataTable";
import { useTranslation } from "react-i18next";
import { useDisclosure, Input, Button } from "@heroui/react";

import CustomerGroupListModal from "../common/modal/CustomerGroupListModal";

import { IoSearchSharp } from "react-icons/io5";
import { IoMdRemoveCircleOutline } from "react-icons/io";
import { useQuery } from "@tanstack/react-query";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";

const columns = [
  {
    name: "id",
    uid: "customer_group_id",
    sortable: true,
    style: { width: "200px" },
  },
  { name: "alias", uid: "customer_group_alias", sortable: true },
  { name: "actions", uid: "actions", style: { width: "50px" } },
];

type CustomerType = {
  customer_group_id: string;
  customer_group_alias: string;
  status_automation: string;
};

interface UserFormCustomerListProps {
  onSelectedItems: (selectedItems: string[]) => void;
  currentCustomerGroup: string[];
  onDeletedItems: (selectedItem: string) => void;
}

export default function UserFormCustomerList({
  onSelectedItems,
  onDeletedItems,
  currentCustomerGroup = [],
}: Readonly<UserFormCustomerListProps>) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [filterValue, setFilterValue] = React.useState("");
  const axiosPrivate = useAxiosPrivate();

  const { data: apiResponse } = useQuery<{ data: CustomerType[], pagination: any }>({
    queryKey: ["CustomerGroupSettings"],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/customers_group/settings?per_page=1000`);
      return response.data;
    },
    staleTime: 6000000,
    gcTime: 6000000,
  });

  const customersList = React.useMemo(() => apiResponse?.data ?? [], [apiResponse]);

  const filteredCustomersList = customersList.filter(customer =>
    currentCustomerGroup.includes(customer.customer_group_id)
  );

  const filteredCustomers = React.useMemo(() => {
    let filtered = [...filteredCustomersList];

    if (filterValue) {
      filtered = filtered.filter(
        (customer) =>
          customer.customer_group_alias
            .toLowerCase()
            .includes(filterValue.toLowerCase()) ||
          customer.customer_group_id
            .toLowerCase()
            .includes(filterValue.toLowerCase()),
      );
    }

    return filtered;
  }, [filterValue, filteredCustomersList]);

  const classNames = React.useMemo(
    () => ({
      wrapper: ["max-h-[382px]"],
      table: ["border-separate", "border-spacing-y-2"],
      th: ["bg-transparent", "text-default-500", "border-b", "border-divider"],
      td: [
        "bg-white",
        "p-3",
        "group-data-[first=true]:first:before:rounded-none",
        "group-data-[first=true]:last:before:rounded-none",
        "group-data-[middle=true]:before:rounded-none",
        "group-data-[last=true]:first:before:rounded-none",
        "group-data-[last=true]:last:before:rounded-none",
      ],
      label: [
        "text-[10px] uppercase text-vistagrey/60 z-0 group-data-[filled=true]:text-vistagrey/60",
      ],
    }),
    [],
  );

  const deleteCustomerGroup = async (customerGroupId) => {
    onDeletedItems(customerGroupId);
  };

  const handleCustomerGroup = React.useCallback(
    (selectedIds: string[] = []) => {
      onSelectedItems(selectedIds);
    },
    [onSelectedItems]
  );

  const renderCell = (customer: CustomerType, columnKey: React.Key) => {
    const cellValue = customer[columnKey as keyof CustomerType];

    switch (columnKey) {
      case "actions":
        return (
          <div className="relative flex justify-end items-center gap-2">
            <button
              className="flex items-center gap-2"
              onClick={() => deleteCustomerGroup(customer.customer_group_id)}
              type="button"
            >
              <IoMdRemoveCircleOutline />
            </button>
          </div>
        );
      default:
        return cellValue;
    }
  };

  const topContent = (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          <Input
            isClearable
            classNames={{
              base: "w-full min-w-[75%]",
              inputWrapper: [
                "border-0",
                "bg-white",
                "h-9",
                "min-h-unit-9",
                "hover:bg-white",
                "group-data-[hover=true]:bg-white",
                "group-data-[focus=true]:bg-white",
              ],
              input: "text-vistagrey",
            }}
            placeholder={t("search")}
            size="md"
            startContent={<IoSearchSharp className="text-default-300" />}
            value={filterValue}
            onClear={() => setFilterValue("")}
            onValueChange={setFilterValue}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex w-full justify-end">
        <Button
          onPress={onOpen}
          className="bg-vistagreen text-white rounded-md p-3 -mt-[60px]"
        >
          {t("add_customer_group")}
        </Button>
      </div>
      <DataTable<CustomerType>
        columns={columns}
        data={filteredCustomers || []}
        renderCell={renderCell}
        keyExtractor={(customer) => customer.customer_group_id}
        initialSortDescriptor={{
          column: "customer_name",
          direction: "ascending",
        }}
        topContent={topContent}
        classNames={classNames}
      />
      {/* <AddUserCustomerGroupModal
        isOpen={isOpen}
        onClose={onClose}
        onNewCustomerGroup={handleNewCustomerGroup}
      /> */}
      <CustomerGroupListModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleCustomerGroup}
         />
    </>
  );
}
